import { faChevronDown, faFloppyDisk, faGear } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "frontend-components";
import { useTranslation } from "react-i18next";

interface CustomizationToolbarProps {
    isCustomizing: boolean;
    isSaving: boolean;
    canDeleteProfile: boolean;
    onStartCustomizing: () => void;
    onRenameProfile: () => void;
    onDeleteProfile: () => void;
    onCancel: () => void;
    onSave: () => void;
    controlsId: string;
}

export function CustomizationToolbar({
    isCustomizing,
    isSaving,
    canDeleteProfile,
    onStartCustomizing,
    onRenameProfile,
    onDeleteProfile,
    onCancel,
    onSave,
    controlsId,
}: CustomizationToolbarProps) {
    const { t } = useTranslation();

    if (isCustomizing) {
        return (
            <div className="hidden items-center gap-2 lg:flex">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={isSaving}
                    aria-controls={controlsId}
                    className="rounded-full px-3.5 py-[5px] text-xs font-medium"
                >
                    {t("common.cancel")}
                </Button>
                <Button
                    type="button"
                    variant="fill"
                    onClick={onSave}
                    disabled={isSaving}
                    aria-controls={controlsId}
                    className="items-center gap-2 rounded-full border border-compendia-foreground bg-compendia-greige-40 px-3.5 py-[5px] text-xs font-medium text-compendia-foreground transition-colors duration-200"
                >
                    <FontAwesomeIcon icon={faFloppyDisk} />
                    {isSaving ? t("common.saving") : t("common.save")}
                </Button>
            </div>
        );
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <Button
                        type="button"
                        variant="outline"
                        disabled={isSaving}
                        className="hidden items-center gap-2 rounded-full border border-compendia-border bg-compendia-card px-3.5 py-[5px] text-xs font-medium text-compendia-gray-60 transition-colors duration-200 hover:border-compendia-foreground hover:text-compendia-foreground lg:inline-flex"
                    >
                        <FontAwesomeIcon icon={faGear} />
                        {t("dashboard.customizeDashboard")}
                        <FontAwesomeIcon icon={faChevronDown} />
                    </Button>
                }
            />
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onStartCustomizing}>
                    {t("dashboard.editDashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onRenameProfile}>
                    {t("dashboard.renameProfile")}
                </DropdownMenuItem>
                {canDeleteProfile && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={onDeleteProfile}>
                            {t("dashboard.deleteProfile")}
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
